import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

// Floating "Report Issue" FAB — always available across the app so any
// staff member can file a complaint/incident in 10 seconds without
// hunting for Team Hub. Stacked above the existing Help button (which
// lives at bottom-6 right-6) so they don't overlap.
export default function ReportIssueFab({ user }) {
  const [open, setOpen] = useState(false);

  // Hide when not authenticated. (AppLayout also gates this, but defense
  // in depth — wouldn't want it to flash for logged-out users.)
  if (!user) return null;

  return (
    <>
      <button
        data-testid="report-issue-fab"
        onClick={() => setOpen(true)}
        title="Report an issue"
        className="fixed bottom-24 right-6 z-40 inline-flex items-center gap-2 h-12 pl-3 pr-5 rounded-full bg-rose-600 text-white shadow-lg shadow-rose-600/30 hover:bg-rose-700 hover:shadow-rose-700/40 transition-all">
        <span className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
          <AlertTriangle className="w-4 h-4" strokeWidth={2.2} />
        </span>
        <span className="text-sm font-medium hidden sm:inline">Report Issue</span>
      </button>
      {open && <ReportIssueModal onClose={() => setOpen(false)} />}
    </>
  );
}

function ReportIssueModal({ onClose }) {
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [category, setCategory] = useState("general");
  const [severity, setSeverity] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [locationId, setLocationId] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch in parallel to keep "tap to open" snappy.
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      api.get("/escalations/categories"),
      api.get("/locations"),
    ]).then(([cats, locs]) => {
      if (cancelled) return;
      if (cats.status === "fulfilled") setCategories(cats.value.data || []);
      if (locs.status === "fulfilled") {
        const list = locs.value.data || [];
        setLocations(list);
        if (list[0]?.location_id) setLocationId(list[0].location_id);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim()) { toast.error("Add a quick title"); return; }
    if (!locationId) { toast.error("Pick a location"); return; }
    setSaving(true);
    try {
      await api.post("/escalations", {
        title: title.trim(),
        description: description.trim(),
        category,
        severity: severity || undefined,
        location_id: locationId,
      });
      toast.success("Issue submitted — routed to the right team");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}
      data-testid="report-issue-modal">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Report an issue</h2>
              <p className="text-[11px] text-slate-500">Gets routed to the right person automatically</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="report-issue-close"
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-3.5">
          {/* Category chips — faster than a dropdown */}
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">What's the issue?</label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {categories.map(c => (
                <button key={c.id} type="button" onClick={() => setCategory(c.id)}
                  data-testid={`report-cat-${c.id}`}
                  className={`text-xs font-medium h-10 px-3 rounded-xl border-2 transition-colors text-left ${
                    category === c.id
                      ? "border-rose-500 bg-rose-50 text-rose-700"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Title</label>
            <Input value={title} onChange={e => setTitle(e.target.value)}
              data-testid="report-issue-title" autoFocus
              placeholder="e.g. Guest unhappy with cut at chair 4"
              className="mt-1.5 h-10 border-slate-200" />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Details (optional)</label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
              data-testid="report-issue-description"
              placeholder="What happened, what they want, any context..."
              className="mt-1.5 resize-none border-slate-200" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Location</label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger className="mt-1.5 h-10 border-slate-200" data-testid="report-issue-location"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent>
                  {locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Urgency (optional)</label>
              <Select value={severity || "default"} onValueChange={v => setSeverity(v === "default" ? "" : v)}>
                <SelectTrigger className="mt-1.5 h-10 border-slate-200" data-testid="report-issue-severity"><SelectValue placeholder="Default" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default for category</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}
              className="border-slate-200 rounded-full px-5">Cancel</Button>
            <Button type="submit" disabled={saving}
              data-testid="report-issue-submit"
              className="bg-rose-600 text-white hover:bg-rose-700 rounded-full px-5">
              {saving ? "Submitting..." : "Submit issue"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
